import { useMemo } from 'react'
import {
  type MomentWorkflow,
  useReadMomentWorkflows,
  type WorkflowResource,
} from '@opallabs/api'
import { type WorkflowSummary } from '@opallabs/concepts'
import {
  type CalendarWorkflowSummaries,
  workflowSummaryKey,
} from '@opallabs/filters'
import { workflowSummaryFromState } from '@opallabs/satellite'
import { type CalendarItem, type CalendarItemKind } from '../types'

/**
 * The kind these summaries are read for. Only moments carry a workflow today;
 * blocks are next, and they will arrive under a key of their own rather than
 * displace this one.
 */
const KIND: CalendarItemKind = 'moment'

const summaryOf = (workflow: WorkflowResource): WorkflowSummary =>
  workflowSummaryFromState({
    approved: workflow.attributes.is_approved,
    completedStages: workflow.meta?.completed_stages,
    declinedAssignments:
      workflow.meta?.latest_active_stage?.assignment_counts.declined,
    latestStageType: workflow.meta?.latest_active_stage?.stage_type,
    status: workflow.attributes.status,
  })

/**
 * Whether anyone has set the workflow up. A workflow record with no stages is
 * an empty shell: nothing on it says what state the work is in, so the grid
 * draws no mark for it and the filter reads it as work that has not started —
 * the same as no workflow at all. Counts the read did not expose are taken as
 * stages, not as none.
 */
const hasStages = (workflow: WorkflowResource): boolean => {
  const counts = workflow.meta?.stage_type_count
  if (counts === undefined) return true

  return counts.approval + counts.task + counts.circuit_breaker > 0
}

const toSummariesByKey = (
  found: MomentWorkflow[]
): ReadonlyMap<string, WorkflowSummary> =>
  new Map(
    found
      .filter(({ workflow }) => hasStages(workflow))
      .map(({ momentId, workflow }) => [
        workflowSummaryKey(KIND, momentId),
        summaryOf(workflow),
      ])
  )

const NOTHING_KNOWN: CalendarWorkflowSummaries = {
  byKey: new Map(),
  isLoaded: false,
}

/**
 * A short, stable stand-in for a set of moments: FNV-1a over the sorted ids.
 * A thousand ids would make a cache key nobody can read, and the key still has
 * to move when the set does — a window read again with a moment added must not
 * keep answering out of the summaries the old set was read into.
 */
const hashMomentIds = (momentIds: readonly string[]): string => {
  const sorted = [...momentIds].sort().join(',')
  let hash = 0x811c9dc5

  for (let index = 0; index < sorted.length; index += 1) {
    hash ^= sorted.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }

  return (hash >>> 0).toString(36)
}

/**
 * The state of the workflow over every moment in the window: the mark the grid
 * and the panel draw on a moment and on the content under it, in the shape the
 * Status filter reads too.
 *
 * Until the answer arrives the map says `isLoaded: false`, no item wears a mark
 * and the Status filter has no opinion about any moment. Once it has arrived, a
 * moment with no workflow — or with one nobody has given a stage — is absent
 * from the map, which the filter reads as work that has not started.
 */
export const useWorkflowSummaries = ({
  areItemsForWindow,
  items,
  window,
}: {
  /**
   * False while `items` are still the window before this one. The month a
   * person is leaving stays on screen until the next one arrives, and reading
   * then would ask for last month's moments under this month's name.
   */
  areItemsForWindow: boolean
  items: readonly CalendarItem[]
  /** The window these items were read for. */
  window: string
}): CalendarWorkflowSummaries => {
  const momentIds = useMemo(
    () => items.flatMap((item) => (item.kind === KIND ? [item.id] : [])),
    [items]
  )
  // The month carries each moment's legacy id, which is all the workflows are
  // asked by, so none of them has to be looked up.
  const legacyIds = useMemo(
    () =>
      new Map(
        items.flatMap((item) =>
          item.kind === KIND && item.legacyId !== undefined
            ? [[item.id, item.legacyId] as const]
            : []
        )
      ),
    [items]
  )

  // The window alone would go on answering out of moments it no longer holds,
  // so the moments in it are named too — as a hash, to keep the key readable.
  const scope = useMemo(
    () => `${window}:${hashMomentIds(momentIds)}`,
    [momentIds, window]
  )

  const { data, isSuccess } = useReadMomentWorkflows({
    enabled: areItemsForWindow,
    legacyIds,
    momentIds,
    scope,
    select: toSummariesByKey,
  })

  return useMemo(
    () => (isSuccess ? { byKey: data, isLoaded: true } : NOTHING_KNOWN),
    [data, isSuccess]
  )
}
